import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { documentTemplatesApi, docxTemplatesApi, personnelApi, pdvApi } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { 
  FileText, Plus, Download, Printer, Eye, FileSignature, 
  Users, Building, Upload, Trash2, CheckSquare, FileDown, UserCheck
} from 'lucide-react';

const TEMPLATE_CATEGORIES = {
  informazione: { label: 'Informazione', color: 'bg-blue-500' },
  attestato: { label: 'Attestato', color: 'bg-green-500' },
  designazione: { label: 'Designazione', color: 'bg-purple-500' },
  incarico: { label: 'Incarico', color: 'bg-orange-500' },
  consegna: { label: 'Consegna', color: 'bg-yellow-500' },
  dpi: { label: 'DPI', color: 'bg-red-500' },
  custom: { label: 'Personalizzato', color: 'bg-gray-500' },
};

// Safety role filters for specific documents
const SAFETY_ROLE_FILTERS = {
  antincendio: { key: 'is_fire_safety_officer', label: 'Addetto Antincendio' },
  primo_soccorso: { key: 'is_first_aid_officer', label: 'Addetto Primo Soccorso' },
  emergenze: { key: 'is_emergency_officer', label: 'Addetto Emergenze' },
  rls: { key: 'is_rls', label: 'RLS' },
  preposto: { key: 'is_preposto', label: 'Preposto' },
  aspp: { key: 'is_aspp', label: 'Addetto SPP' },
  rspp_datore: { key: 'is_rspp_datore_lavoro', label: 'RSPP Datore di Lavoro' },
};

export default function DocumentGeneratorPage() {
  const { user } = useAuth();
  const { currentPdvId } = useCurrentPdv();
  const [docxTemplates, setDocxTemplates] = useState([]);
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filterPdv, setFilterPdv] = useState('all');
  const [activeTab, setActiveTab] = useState('templates');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [selectedPdvForBatch, setSelectedPdvForBatch] = useState('');
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState([]);
  const [safetyRoleFilter, setSafetyRoleFilter] = useState('all');
  // Multi-select safety roles: array of keys e.g. ['antincendio', 'primo_soccorso']
  const [safetyRoleFilters, setSafetyRoleFilters] = useState([]);

  const matchesAnyRole = (person) => {
    if (safetyRoleFilters.length === 0) return true;
    return safetyRoleFilters.some((k) => {
      const f = SAFETY_ROLE_FILTERS[k];
      return f && person[f.key] === true;
    });
  };
  const toggleSafetyRole = (k) => {
    setSafetyRoleFilters((prev) => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  };

  // === Customizzazioni per generazione ===
  const todayISO = new Date().toISOString().slice(0, 10);
  const [docDate, setDocDate] = useState(todayISO);
  const [corsoDate, setCorsoDate] = useState(todayISO);
  const [luogoDesignazione, setLuogoDesignazione] = useState('');
  const [dpiSelected, setDpiSelected] = useState([]);
  const [dpiCustom, setDpiCustom] = useState('');
  // Default true: ogni dipendente del batch riceve i DPI della SUA mansione.
  // Se l'utente seleziona DPI manualmente, l'override manuale vince.
  const [dpiAutoByMansione, setDpiAutoByMansione] = useState(true);
  // Flow "Incarico Professionale RSPP" (per UL, no dipendente)
  const [ulDialogOpen, setUlDialogOpen] = useState(false);
  const [ulSelectedPdv, setUlSelectedPdv] = useState('');
  const [ulRsppNome, setUlRsppNome] = useState('');
  const [ulRsppCognome, setUlRsppCognome] = useState('');
  const [ulRsppQualifica, setUlRsppQualifica] = useState('Ing.');
  const [ulRsppCF, setUlRsppCF] = useState('');
  const [ulDocDate, setUlDocDate] = useState(todayISO);

  const DPI_OPTIONS = [
    'Scarpe antinfortunistiche',
    'Scarpe antinfortunistiche S3',
    'Scarpe antinfortunistiche antiscivolo',
    'Scarpe isolanti',
    'Casco di protezione',
    'Cuffia/Copricapo',
    'Guanti di protezione',
    'Guanti antitaglio',
    'Guanti antitaglio/antifiamma',
    'Guanti dielettrici',
    'Guanti di protezione chimica',
    'Guanti monouso',
    'Occhiali di protezione',
    'Maschera/Respiratore',
    'Maschera saldatura/Schermo',
    'Mascherina FFP2/FFP3',
    'Cuffie antirumore',
    'Imbragatura/DPI anticaduta',
    'Indumenti alta visibilità',
    'Grembiule/Camice',
    'Grembiule cuoio',
    'Camice',
    'Tuta da lavoro',
    'Nessun DPI specifico (videoterminale)',
  ];

  // v98: template che si generano SOLO con UL (no dipendente):
  // Incarico RSPP, Dichiarazione Sicurezza, DVR, Autocertificazione, ecc.
  const isIncaricoTemplate = (tpl) => {
    if (!tpl) return false;
    const n = (tpl.name || '').toLowerCase();
    const ulOnlyKeywords = [
      'incarico', 'rspp',
      'dichiarazione', 'sicurezza luoghi', 'luoghi di lavoro',
      'autocertificazione', 'dvr', 'valutazione dei rischi',
      'datore di lavoro', 'nomina', 'lettera incarico',
    ];
    return ulOnlyKeywords.some(k => n.includes(k));
  };
  // v98: alcuni template UL-only NON richiedono i campi RSPP (es. Dichiarazione Sicurezza,
  // DVR, Autocertificazione, Nomina) — solo UL + data.
  const templateNeedsRspp = (tpl) => {
    if (!tpl) return false;
    const n = (tpl.name || '').toLowerCase();
    return n.includes('incarico') || n.includes('rspp');
  };
  const isDpiTemplate = (tpl) => {
    if (!tpl) return false;
    const n = (tpl.name || '').toLowerCase();
    return (tpl.category === 'dpi') || n.includes('dpi') || n.includes('art. 18') || n.includes('art 18');
  };
  const isDesignazioneTemplate = (tpl) => {
    if (!tpl) return false;
    const n = (tpl.name || '').toLowerCase();
    return n.includes('designazione');
  };

  const toggleDpi = (item) => {
    setDpiSelected((prev) => prev.includes(item) ? prev.filter(x => x !== item) : [...prev, item]);
  };
  const buildDpiList = () => {
    const list = [...dpiSelected];
    const extra = (dpiCustom || '').split(',').map(s => s.trim()).filter(Boolean);
    return [...list, ...extra];
  };

  const [uploadForm, setUploadForm] = useState({
    file: null,
    name: '',
    category: 'custom',
    description: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [docxRes, docsRes, personnelRes, pdvRes] = await Promise.all([
        docxTemplatesApi.getAll().catch(() => ({ data: [] })),
        documentTemplatesApi.getGenerated(),
        personnelApi.getAll(),
        pdvApi.getAll(),
      ]);
      setDocxTemplates(docxRes.data || []);
      setGeneratedDocs(docsRes.data);
      setPersonnel(personnelRes.data);
      setPdvList(pdvRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplateForBatch = (template) => {
    setSelectedTemplate(template);
    setSelectedPdvForBatch('');
    setSelectedPersonnelIds([]);
    // Reset opzioni "extra" per ogni nuova generazione
    setDocDate(todayISO);
    setCorsoDate(todayISO);
    setLuogoDesignazione('');
    setDpiSelected([]);
    setDpiCustom('');
    
    // Auto-detect safety roles based on template name (preselect multi-select)
    const templateNameLower = template.name.toLowerCase();
    const preset = [];
    if (templateNameLower.includes('antincendio') || /\baa\b/.test(templateNameLower)) preset.push('antincendio');
    if (templateNameLower.includes('primo soccorso') || /\bps\b/.test(templateNameLower)) preset.push('primo_soccorso');
    if (templateNameLower.includes('emergenz')) preset.push('emergenze');
    if (templateNameLower.includes('rls')) preset.push('rls');
    if (templateNameLower.includes('preposto')) preset.push('preposto');
    if (templateNameLower.includes('aspp') || /\bspp\b/.test(templateNameLower)) preset.push('aspp');
    setSafetyRoleFilters(preset);
    // Legacy single-select compat
    setSafetyRoleFilter(preset[0] || 'all');

    // Incarico Professionale → flow per UL (no dipendente)
    if (isIncaricoTemplate(template)) {
      // Pre-compila con i dati dell'utente loggato (RSPP / professionista)
      const fullName = (user?.full_name || '').trim();
      const parts = fullName.split(/\s+/);
      const nome = parts[0] || '';
      const cognome = parts.slice(1).join(' ') || '';
      setUlSelectedPdv(currentPdvId || ''); // usa UL del banner globale se selezionata
      setUlRsppNome(nome);
      setUlRsppCognome(cognome);
      setUlRsppQualifica(user?.titolo_professionale || 'Ing.');
      setUlRsppCF(user?.fiscal_code || '');
      setUlDocDate(todayISO);
      setUlDialogOpen(true);
      return;
    }

    // Per template "per dipendente": se UL globale è selezionata, usala come default
    if (currentPdvId && !selectedPdvForBatch) {
      setSelectedPdvForBatch(currentPdvId);
    }

    setBatchDialogOpen(true);
  };

  const handleGenerateUl = async () => {
    if (!selectedTemplate || !ulSelectedPdv) {
      toast.error('Seleziona Unità Locale');
      return;
    }
    const needsRspp = templateNeedsRspp(selectedTemplate);
    if (needsRspp && (!ulRsppNome || !ulRsppCognome)) {
      toast.error('Inserisci nome e cognome del Professionista (RSPP)');
      return;
    }
    setGenerating(true);
    try {
      const additional = {
        data_documento: ulDocDate,
        nome_rspp: ulRsppNome,
        cognome_rspp: ulRsppCognome,
        qualifica_rspp: ulRsppQualifica,
        cf_rspp: ulRsppCF,
      };
      const params = new URLSearchParams({
        pdv_id: ulSelectedPdv,
        additional_data: JSON.stringify(additional),
      });
      const token = sessionStorage.getItem('token');
      const res = await fetch(`/api/docx-templates/${selectedTemplate.id}/generate-ul?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const t = await res.text();
        console.error('generate-ul failed', res.status, t);
        toast.error(res.status === 401 ? 'Sessione scaduta, ri-accedi' : 'Errore generazione');
        return;
      }
      const data = await res.json();
      toast.success('Incarico generato!');
      setUlDialogOpen(false);
      loadData();
      // Auto-download
      const dlRes = await fetch(`/api/generated-documents/${data.id}/download?token=${token}`);
      if (dlRes.ok) {
        const blob = await dlRes.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedTemplate.name}_${ulDocDate}.docx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error(e);
      toast.error('Errore durante la generazione');
    } finally {
      setGenerating(false);
    }
  };

  const handlePdvSelect = (pdvId) => {
    setSelectedPdvForBatch(pdvId);
    // Auto-select personnel based on safety role filter
    let pdvPersonnel = personnel.filter(p => p.pdv_id === pdvId);
    
    // Apply safety role filter if set
    if (safetyRoleFilters.length > 0) {
      pdvPersonnel = pdvPersonnel.filter(matchesAnyRole);
    }
    
    setSelectedPersonnelIds(pdvPersonnel.map(p => p.id));
  };

  const handleSafetyRoleFilterChange = (filter) => {
    setSafetyRoleFilter(filter);
    
    // Re-filter personnel if PDV is already selected
    if (selectedPdvForBatch) {
      let pdvPersonnel = personnel.filter(p => p.pdv_id === selectedPdvForBatch);
      
      if (filter !== 'all' && SAFETY_ROLE_FILTERS[filter]) {
        const filterKey = SAFETY_ROLE_FILTERS[filter].key;
        pdvPersonnel = pdvPersonnel.filter(p => p[filterKey] === true);
      }
      
      setSelectedPersonnelIds(pdvPersonnel.map(p => p.id));
    }
  };

  const handleTogglePersonnel = (personnelId) => {
    setSelectedPersonnelIds(prev => {
      const next = prev.includes(personnelId)
        ? prev.filter(id => id !== personnelId)
        : [...prev, personnelId];
      // Se sto SELEZIONANDO il PRIMO dipendente e il template è DPI,
      // auto-popola i DPI suggeriti in base alla sua mansione SOLO
      // quando NON è attiva la modalità "auto per mansione" (in quel
      // caso il backend calcola i DPI per ogni dipendente del batch).
      if (
        isDpiTemplate(selectedTemplate) &&
        !dpiAutoByMansione &&
        !prev.includes(personnelId) &&
        next.length === 1 &&
        dpiSelected.length === 0
      ) {
        const p = personnel.find((x) => x.id === personnelId);
        const mans = p?.job_title || '';
        if (mans) {
          api.get('/dpi-suggestions', { params: { mansione: mans } })
            .then((r) => {
              const list = r.data?.dpi || [];
              if (list.length > 0) {
                setDpiSelected(list);
                toast.success(`DPI suggeriti per "${mans}": ${list.length} elementi`);
              }
            })
            .catch(() => {});
        }
      }
      return next;
    });
  };

  const handleSelectAllPersonnel = () => {
    let pdvPersonnel = personnel.filter(p => p.pdv_id === selectedPdvForBatch);
    
    // Apply safety role filter (multi-select)
    if (safetyRoleFilters.length > 0) {
      pdvPersonnel = pdvPersonnel.filter(matchesAnyRole);
    } else if (safetyRoleFilter !== 'all' && SAFETY_ROLE_FILTERS[safetyRoleFilter]) {
      const filterKey = SAFETY_ROLE_FILTERS[safetyRoleFilter].key;
      pdvPersonnel = pdvPersonnel.filter(p => p[filterKey] === true);
    }
    
    if (selectedPersonnelIds.length === pdvPersonnel.length) {
      setSelectedPersonnelIds([]);
    } else {
      setSelectedPersonnelIds(pdvPersonnel.map(p => p.id));
    }
  };

  const handleBatchGenerate = async () => {
    if (!selectedTemplate || selectedPersonnelIds.length === 0) {
      toast.error('Seleziona almeno un dipendente');
      return;
    }

    setGenerating(true);
    const generatedIds = [];
    let successCount = 0;
    let errorCount = 0;

    const additional = {
      data_documento: docDate,
      data_corso: corsoDate,
    };
    if (luogoDesignazione) additional.luogo_designazione = luogoDesignazione;
    const dpiList = buildDpiList();
    if (dpiList.length > 0) {
      additional.dpi_list = dpiList;
    } else if (isDpiTemplate(selectedTemplate) && dpiAutoByMansione) {
      // Niente DPI manuali: chiediamo al backend di calcolare per mansione
      // (per OGNI dipendente del batch, individualmente)
      additional.dpi_auto_by_mansione = true;
    }

    for (const personnelId of selectedPersonnelIds) {
      try {
        const response = await docxTemplatesApi.generate(
          selectedTemplate.id,
          personnelId,
          additional
        );
        generatedIds.push(response.data.id);
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Errore per ${personnelId}:`, error);
      }
    }

    setGenerating(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} documenti generati!`);
      loadData();
      
      // Auto-download all generated documents as ZIP
      if (generatedIds.length > 0) {
        try {
          const token = sessionStorage.getItem('token');
          const response = await fetch(`/api/generated-documents/batch-download?token=${token}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(generatedIds),
          });

          if (!response.ok) {
            const errText = await response.text();
            console.error('Batch download failed:', response.status, errText);
            toast.error('Download non riuscito: ' + (response.status === 401 ? 'sessione scaduta, ri-accedi' : `errore ${response.status}`));
            return;
          }

          // Verify it's a ZIP (PK\x03\x04 signature)
          const blob = await response.blob();
          if (blob.size < 22) {
            toast.error('Il file scaricato è vuoto. I documenti generati non hanno un file allegato (probabilmente template HTML).');
            return;
          }
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${selectedTemplate.name}_${new Date().toISOString().slice(0,10)}.zip`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Download error:', error);
          toast.error('Errore durante il download');
        }
      }
    }
    
    if (errorCount > 0) {
      toast.error(`${errorCount} documenti non generati`);
    }

    setBatchDialogOpen(false);
  };

  const handleUploadTemplate = async () => {
    if (!uploadForm.file || !uploadForm.name) {
      toast.error('Inserisci nome e file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      
      await docxTemplatesApi.upload(formData, {
        name: uploadForm.name,
        category: uploadForm.category,
        description: uploadForm.description,
      });
      
      toast.success('Template caricato!');
      setUploadDialogOpen(false);
      setUploadForm({ file: null, name: '', category: 'custom', description: '' });
      loadData();
    } catch (error) {
      toast.error('Errore nel caricamento');
    }
  };

  const handleDeleteDocxTemplate = async (id) => {
    if (!window.confirm('Eliminare questo template?')) return;
    try {
      await docxTemplatesApi.delete(id);
      toast.success('Template eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleDownload = (docId) => {
    const url = documentTemplatesApi.getGeneratedDownload(docId);
    window.open(url, '_blank');
  };

  const handleDeleteGenerated = async (docId) => {
    if (!window.confirm('Eliminare questo documento generato? L\'azione non è reversibile.')) return;
    try {
      await documentTemplatesApi.deleteGenerated(docId);
      toast.success('Documento eliminato');
      setSelectedDocs((prev) => prev.filter((id) => id !== docId));
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione documento');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Seleziona almeno un documento');
      return;
    }
    if (!window.confirm(`Eliminare ${selectedDocs.length} documento/i selezionato/i? L'azione non è reversibile.`)) return;
    let ok = 0;
    for (const id of selectedDocs) {
      try {
        await documentTemplatesApi.deleteGenerated(id);
        ok += 1;
      } catch (e) {
        // continue
      }
    }
    setSelectedDocs([]);
    if (ok > 0) toast.success(`${ok} documento/i eliminato/i`);
    else toast.error('Nessun documento eliminato');
    loadData();
  };

  const handleSelectDoc = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocs.length === filteredDocs.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(filteredDocs.map(d => d.id));
    }
  };

  const handleBatchDownload = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Seleziona almeno un documento');
      return;
    }
    
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/generated-documents/batch-download?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedDocs),
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error('Batch download failed:', response.status, errText);
        toast.error('Download non riuscito: ' + (response.status === 401 ? 'sessione scaduta, ri-accedi' : `errore ${response.status}`));
        return;
      }

      const blob = await response.blob();
      if (blob.size < 22) {
        toast.error('Il file scaricato è vuoto. I documenti selezionati non hanno un file scaricabile (template HTML).');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documenti_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download avviato!');
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const getPdvName = (pdvId) => {
    const pdv = pdvList.find(p => p.id === pdvId);
    return pdv ? pdv.name : 'N/D';
  };

  const filteredDocs = filterPdv === 'all'
    ? generatedDocs
    : generatedDocs.filter(d => d.pdv_id === filterPdv);

  // Filter personnel by PDV and safety roles (multi-select takes precedence)
  const pdvPersonnelList = personnel.filter(p => {
    if (p.pdv_id !== selectedPdvForBatch) return false;
    if (safetyRoleFilters.length > 0) return matchesAnyRole(p);
    if (safetyRoleFilter === 'all') return true;
    if (SAFETY_ROLE_FILTERS[safetyRoleFilter]) {
      return p[SAFETY_ROLE_FILTERS[safetyRoleFilter].key] === true;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="document-generator-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl">Generatore Documenti</h1>
          <p className="text-muted-foreground mt-1">Genera documenti Word per tutto il personale di una UL</p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="w-4 h-4 mr-2" />
          Carica Template Word
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileSignature className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{docxTemplates.length}</p>
                <p className="text-sm text-muted-foreground">Template Word</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{generatedDocs.length}</p>
                <p className="text-sm text-muted-foreground">Documenti Generati</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{personnel.length}</p>
                <p className="text-sm text-muted-foreground">Dipendenti</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Building className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pdvList.length}</p>
                <p className="text-sm text-muted-foreground">UL</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="templates">Template Word</TabsTrigger>
            <TabsTrigger value="generated">Documenti Generati</TabsTrigger>
          </TabsList>
          <Select value={filterPdv} onValueChange={setFilterPdv}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filtra per UL" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le UL</SelectItem>
              {pdvList.map(pdv => (
                <SelectItem key={pdv.id} value={pdv.id}>{pdv.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Word Templates */}
        <TabsContent value="templates">
          {docxTemplates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Upload className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessun template Word caricato</p>
                <p className="text-sm mt-2">Carica i tuoi template .docx con le variabili «NomeVariabile»</p>
                <Button className="mt-4" onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Carica Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {docxTemplates.map((template) => {
                const catInfo = TEMPLATE_CATEGORIES[template.category] || { label: template.category, color: 'bg-gray-500' };
                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="p-3 bg-blue-100 rounded-lg">
                            <FileText className="w-6 h-6 text-blue-600" />
                          </div>
                          <Badge className={catInfo.color}>{catInfo.label}</Badge>
                        </div>
                        <h3 className="font-semibold text-lg mb-2">{template.name}</h3>
                        <p className="text-sm text-muted-foreground mb-2">{template.description || template.original_filename}</p>
                        {template.variables?.length > 0 && (
                          <p className="text-xs text-muted-foreground mb-4">
                            Variabili: {template.variables.slice(0, 3).join(', ')}
                            {template.variables.length > 3 && ` +${template.variables.length - 3}`}
                          </p>
                        )}
                        <div className="flex gap-2">
                          <Button className="flex-1" onClick={() => handleSelectTemplateForBatch(template)}>
                            <Users className="w-4 h-4 mr-2" />
                            Genera per UL
                          </Button>
                          <Button variant="destructive" size="icon" onClick={() => handleDeleteDocxTemplate(template.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Generated Documents */}
        <TabsContent value="generated">
          {filteredDocs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessun documento generato</p>
                <Button className="mt-4" onClick={() => setActiveTab('templates')}>
                  Vai ai Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Batch Actions */}
              <div className="flex items-center gap-4 mb-4 p-4 bg-muted rounded-lg">
                <Checkbox 
                  checked={selectedDocs.length === filteredDocs.length && filteredDocs.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm">
                  {selectedDocs.length > 0 
                    ? `${selectedDocs.length} selezionati`
                    : 'Seleziona tutto'}
                </span>
                <div className="flex-1" />
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={selectedDocs.length === 0}
                  onClick={handleBatchDownload}
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Scarica ZIP ({selectedDocs.length})
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={selectedDocs.length === 0}
                  onClick={handleBatchDelete}
                  data-testid="batch-delete-docs-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina ({selectedDocs.length})
                </Button>
              </div>

              <div className="space-y-3">
                {filteredDocs.map((doc) => (
                  <motion.div
                    key={doc.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className={selectedDocs.includes(doc.id) ? 'ring-2 ring-primary' : ''}>
                      <CardContent className="py-4">
                        <div className="flex items-center gap-4">
                          <Checkbox 
                            checked={selectedDocs.includes(doc.id)}
                            onCheckedChange={() => handleSelectDoc(doc.id)}
                          />
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{doc.template_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {doc.personnel_name} • {getPdvName(doc.pdv_id)} • {new Date(doc.generated_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => handleDownload(doc.id)} data-testid={`download-doc-${doc.id}`}>
                            <Download className="w-4 h-4 mr-2" />
                            Scarica
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteGenerated(doc.id)}
                            data-testid={`delete-doc-${doc.id}`}
                            title="Elimina documento"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Batch Generate Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Genera: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Seleziona una UL per generare automaticamente il documento per i dipendenti
            </p>

            {/* === Date personalizzate === */}
            <div className="grid grid-cols-2 gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50/50">
              <div className="space-y-1">
                <Label className="text-xs">Data documento *</Label>
                <Input
                  type="date"
                  value={docDate}
                  onChange={(e) => setDocDate(e.target.value)}
                  data-testid="doc-date-input"
                />
                <p className="text-[10px] text-muted-foreground">Va sui campi «Data_Odierna», «Data_consegna_DPI», «Data_Designazione»</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Data corso / consegna</Label>
                <Input
                  type="date"
                  value={corsoDate}
                  onChange={(e) => setCorsoDate(e.target.value)}
                  data-testid="corso-date-input"
                />
                <p className="text-[10px] text-muted-foreground">Va sui campi «Periodo_dal», «Periodo_al», «Data_corso»</p>
              </div>
            </div>

            {/* === Luogo Designazione (solo per Designazione AA/PS) === */}
            {isDesignazioneTemplate(selectedTemplate) && (
              <div className="space-y-1 p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                <Label className="text-xs">Luogo della designazione</Label>
                <Input
                  value={luogoDesignazione}
                  onChange={(e) => setLuogoDesignazione(e.target.value)}
                  placeholder="Es. Roma (lascia vuoto per usare la città dell'UL)"
                  data-testid="luogo-designazione-input"
                />
              </div>
            )}

            {/* === DPI multi-select (solo per Art. 18 / template DPI) === */}
            {isDpiTemplate(selectedTemplate) && (
              <div className="space-y-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50/50">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label className="text-xs font-semibold">DPI consegnati ai dipendenti</Label>
                  <label className="flex items-center gap-2 text-[11px] text-emerald-900 cursor-pointer bg-white/60 rounded px-2 py-1 border border-emerald-200">
                    <input
                      type="checkbox"
                      checked={dpiAutoByMansione}
                      onChange={(e) => {
                        setDpiAutoByMansione(e.target.checked);
                        if (e.target.checked) {
                          // attivando auto: pulisco la selezione manuale
                          setDpiSelected([]);
                          setDpiCustom('');
                        }
                      }}
                      data-testid="dpi-auto-mansione-toggle"
                    />
                    <span><strong>Auto per mansione</strong> di ogni dipendente</span>
                  </label>
                </div>
                {dpiAutoByMansione && (
                  <p className="text-[11px] text-emerald-800 bg-white/60 rounded px-2 py-1 border border-emerald-100">
                    🧠 Ogni dipendente del batch riceverà <strong>i DPI specifici della sua mansione</strong> (calcolati automaticamente). Per forzare una lista UNICA per tutti, deseleziona l'opzione e scegli i DPI qui sotto.
                  </p>
                )}
                {!dpiAutoByMansione && selectedPersonnelIds.length > 0 && (
                  <button
                    type="button"
                    onClick={async () => {
                      const p = personnel.find((x) => x.id === selectedPersonnelIds[0]);
                      const mans = p?.job_title || '';
                      if (!mans) { toast.error('Il dipendente non ha una mansione impostata'); return; }
                      try {
                        const r = await api.get('/dpi-suggestions', { params: { mansione: mans } });
                        setDpiSelected(r.data?.dpi || []);
                        toast.success(`DPI suggeriti per "${mans}"`);
                      } catch { toast.error('Errore caricamento DPI suggeriti'); }
                    }}
                    className="text-[11px] text-emerald-700 hover:underline"
                    data-testid="reload-dpi-suggestions"
                  >
                    🔄 Suggerisci DPI per la mansione del primo selezionato
                  </button>
                )}
                <div className={`grid grid-cols-2 gap-2 ${dpiAutoByMansione ? 'opacity-50 pointer-events-none' : ''}`}>
                  {DPI_OPTIONS.map((opt) => (
                    <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dpiSelected.includes(opt)}
                        onChange={() => toggleDpi(opt)}
                        data-testid={`dpi-${opt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Altri DPI (separati da virgola)</Label>
                  <Input
                    value={dpiCustom}
                    onChange={(e) => setDpiCustom(e.target.value)}
                    placeholder="Es. Maschera saldatura, Ginocchiere"
                    data-testid="dpi-custom-input"
                  />
                </div>
                {buildDpiList().length > 0 && (
                  <p className="text-[11px] text-emerald-700">
                    {buildDpiList().length} DPI selezionati: {buildDpiList().join(', ')}
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Il template Word deve contenere il segnaposto «DPI_Lista_Bullets» (testo) o una tabella con «DPI_ROW» nella riga modello per la popolazione automatica.
                </p>
              </div>
            )}

            {/* Safety Role Filter (multi-select) */}
            <div className="space-y-2">
              <Label>Filtra per Ruolo di Sicurezza (multi-selezione)</Label>
              <div className="grid grid-cols-2 gap-2 p-3 rounded-lg border bg-muted/30">
                {Object.entries(SAFETY_ROLE_FILTERS).map(([k, def]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={safetyRoleFilters.includes(k)}
                      onChange={() => toggleSafetyRole(k)}
                      data-testid={`safety-role-${k}`}
                    />
                    {def.label}
                  </label>
                ))}
              </div>
              {safetyRoleFilters.length > 0 ? (
                <p className="text-xs text-blue-600">
                  ℹ️ Vengono mostrati i dipendenti con almeno uno dei ruoli: {safetyRoleFilters.map(k => SAFETY_ROLE_FILTERS[k]?.label).join(', ')}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">Nessun filtro attivo: vengono mostrati tutti i dipendenti dell'UL.</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Seleziona UL *</Label>
              <Select value={selectedPdvForBatch} onValueChange={handlePdvSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona UL..." />
                </SelectTrigger>
                <SelectContent>
                  {pdvList.map(pdv => {
                    let count = personnel.filter(p => p.pdv_id === pdv.id).length;
                    if (safetyRoleFilters.length > 0) {
                      count = personnel.filter(p => p.pdv_id === pdv.id && matchesAnyRole(p)).length;
                    } else if (safetyRoleFilter !== 'all' && SAFETY_ROLE_FILTERS[safetyRoleFilter]) {
                      const filterKey = SAFETY_ROLE_FILTERS[safetyRoleFilter].key;
                      count = personnel.filter(p => p.pdv_id === pdv.id && p[filterKey] === true).length;
                    }
                    const lbl = safetyRoleFilters.length > 0
                      ? `incaricati (${safetyRoleFilters.map(k => SAFETY_ROLE_FILTERS[k]?.label.split(' ').pop()).join('/')})`
                      : (safetyRoleFilter !== 'all' ? SAFETY_ROLE_FILTERS[safetyRoleFilter]?.label : 'dipendenti');
                    return (
                      <SelectItem key={pdv.id} value={pdv.id}>
                        {pdv.name} ({count} {lbl})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {selectedPdvForBatch && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Dipendenti ({pdvPersonnelList.length})</Label>
                  <Button variant="ghost" size="sm" onClick={handleSelectAllPersonnel}>
                    {selectedPersonnelIds.length === pdvPersonnelList.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                  </Button>
                </div>
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {pdvPersonnelList.length === 0 ? (
                    <p className="p-4 text-center text-muted-foreground">
                      {safetyRoleFilters.length > 0
                        ? `Nessun dipendente con i ruoli selezionati in questa UL`
                        : (safetyRoleFilter !== 'all' 
                          ? `Nessun ${SAFETY_ROLE_FILTERS[safetyRoleFilter]?.label} in questa UL`
                          : 'Nessun dipendente in questa UL')}
                    </p>
                  ) : (
                    pdvPersonnelList.map(person => (
                      <div 
                        key={person.id} 
                        className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                        onClick={() => handleTogglePersonnel(person.id)}
                      >
                        <Checkbox 
                          checked={selectedPersonnelIds.includes(person.id)}
                          onCheckedChange={() => handleTogglePersonnel(person.id)}
                        />
                        <UserCheck className="w-4 h-4 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="font-medium">{person.first_name} {person.last_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {person.job_title || 'N/D'} • {person.fiscal_code || 'N/D'}
                            {person.is_fire_safety_officer && ' 🔥'}
                            {person.is_first_aid_officer && ' 🏥'}
                            {person.is_rls && ' 👷'}
                            {person.is_preposto && ' ⚠️'}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-blue-900">
                {selectedPersonnelIds.length > 0 
                  ? `Verranno generati ${selectedPersonnelIds.length} documenti Word e scaricati in un file ZIP`
                  : 'Seleziona una UL e i dipendenti per generare i documenti'}
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setBatchDialogOpen(false)} className="flex-1">
                Annulla
              </Button>
              <Button 
                onClick={handleBatchGenerate} 
                className="flex-1" 
                disabled={selectedPersonnelIds.length === 0 || generating}
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generazione...
                  </>
                ) : (
                  <>
                    <FileDown className="w-4 h-4 mr-2" />
                    Genera e Scarica ({selectedPersonnelIds.length})
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* === UL Dialog (Incarico Professionale RSPP - per UL, NO dipendente) === */}
      <Dialog open={ulDialogOpen} onOpenChange={setUlDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ul-dialog">
          <DialogHeader>
            <DialogTitle>{templateNeedsRspp(selectedTemplate) ? 'Incarico Professionale' : 'Documento per Unità Locale'}: {selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Questo documento è legato all'<strong>Unità Locale</strong>, non a un singolo dipendente.
            </p>
            <div className="space-y-1">
              <Label>Unità Locale *</Label>
              <Select value={ulSelectedPdv} onValueChange={setUlSelectedPdv}>
                <SelectTrigger data-testid="ul-pdv-select">
                  <SelectValue placeholder="Seleziona UL..." />
                </SelectTrigger>
                <SelectContent>
                  {pdvList.map(pdv => (
                    <SelectItem key={pdv.id} value={pdv.id}>
                      {pdv.name} - {pdv.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {ulSelectedPdv && (() => {
                const p = pdvList.find(x => x.id === ulSelectedPdv);
                if (!p) return null;
                return (
                  <div className="text-[11px] text-muted-foreground bg-blue-50 p-2 rounded">
                    <div><strong>{p.company_name || p.name}</strong></div>
                    <div>{p.address} - {p.cap} {p.city} ({p.province})</div>
                    <div>Legale Rappresentante: <strong>{p.manager_name || '—'}</strong>{p.manager_birth_place ? ` (nato a ${p.manager_birth_place}${p.manager_birth_date ? ` il ${p.manager_birth_date}` : ''})` : ''}</div>
                  </div>
                );
              })()}
            </div>

            {templateNeedsRspp(selectedTemplate) && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Qualifica</Label>
                    <Input
                      value={ulRsppQualifica}
                      onChange={(e) => setUlRsppQualifica(e.target.value)}
                      placeholder="Ing."
                      data-testid="rspp-qualifica-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nome RSPP *</Label>
                    <Input
                      value={ulRsppNome}
                      onChange={(e) => setUlRsppNome(e.target.value)}
                      placeholder="Mario"
                      data-testid="rspp-nome-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Cognome RSPP *</Label>
                    <Input
                      value={ulRsppCognome}
                      onChange={(e) => setUlRsppCognome(e.target.value)}
                      placeholder="Rossi"
                      data-testid="rspp-cognome-input"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Codice Fiscale RSPP</Label>
                  <Input
                    value={ulRsppCF}
                    onChange={(e) => setUlRsppCF(e.target.value)}
                    placeholder="RSSMRA80A01H501Z"
                    data-testid="rspp-cf-input"
                  />
                </div>
              </>
            )}

            <div className="space-y-1">
              <Label className="text-xs">Data Documento *</Label>
              <Input
                type="date"
                value={ulDocDate}
                onChange={(e) => setUlDocDate(e.target.value)}
                data-testid="ul-doc-date-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setUlDialogOpen(false)} className="flex-1">
                Annulla
              </Button>
              <Button
                onClick={handleGenerateUl}
                disabled={generating || !ulSelectedPdv || (templateNeedsRspp(selectedTemplate) && (!ulRsppNome || !ulRsppCognome))}
                className="flex-1"
                data-testid="generate-ul-btn"
              >
                {generating ? 'Generazione...' : (templateNeedsRspp(selectedTemplate) ? 'Genera Incarico' : 'Genera Documento')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Template Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Carica Template Word</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Carica un file Word (.docx) con variabili nel formato <code className="bg-muted px-1 rounded">«NomeVariabile»</code>
            </p>
            
            <div className="space-y-2">
              <Label>Nome Template *</Label>
              <Input
                value={uploadForm.name}
                onChange={(e) => setUploadForm({...uploadForm, name: e.target.value})}
                placeholder="Es. Verbale Informazione Art. 36"
              />
            </div>

            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select 
                value={uploadForm.category} 
                onValueChange={(v) => setUploadForm({...uploadForm, category: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TEMPLATE_CATEGORIES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                placeholder="Descrizione opzionale"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>File Word (.docx) *</Label>
              <Input
                type="file"
                accept=".docx"
                onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
              />
            </div>

            <div className="bg-muted p-3 rounded-lg text-sm">
              <p className="font-medium mb-2">Variabili supportate:</p>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <span>«Nome_corsista»</span>
                <span>«Cognome_corsista»</span>
                <span>«Cod_Fiscale»</span>
                <span>«Corsista_il»</span>
                <span>«Corsista_Nato»</span>
                <span>«Mansione_unilav»</span>
                <span>«Azienda_organizzatrice»</span>
                <span>«Sede_Azienda»</span>
                <span>«Luogo_Azianda»</span>
                <span>«Provincia»</span>
                <span>«Cap»</span>
                <span>«Nome_Datore_Lavoro»</span>
                <span>«Periodo_dal»</span>
                <span>«Periodo_al»</span>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)} className="flex-1">
                Annulla
              </Button>
              <Button onClick={handleUploadTemplate} className="flex-1" disabled={!uploadForm.file || !uploadForm.name}>
                <Upload className="w-4 h-4 mr-2" />
                Carica Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
